import { LoginCredentials } from '@/types';
import * as authApi from '@/api/auth';
import { storeToken, storeRefreshToken, storeTokenExpires, storeLoginResponse } from './storageService';

/**
 * Handle the complete login flow
 */
export const handleLogin = async (
  credentials: LoginCredentials,
  nextUrl?: string
): Promise<{ requiresTwoFactor: boolean; token?: string }> => {
  // Step 1: Check if 2FA is enabled
  const twoFactorCheck = await authApi.checkTwoFactorEnabled(credentials);

  if (twoFactorCheck.is_enabled) {
    // Step 2: Send 2FA code to user's device
    await authApi.sendTwoFactorCode(credentials);

    return {
      requiresTwoFactor: true,
    };
  }

  // Step 3: Perform login (no 2FA required)
  // Apigee returns { token, refresh, expires } + sets sessionid cookie
  const loginResponse = await authApi.login(credentials);

  // Step 4: Store login response
  await storeLoginResponse(loginResponse);
  if (loginResponse.token) {
    await storeToken(loginResponse.token);
  }
  if (loginResponse.refresh) {
    await storeRefreshToken(loginResponse.refresh);
  }
  if (loginResponse.expires) {
    await storeTokenExpires(loginResponse.expires);
  }

  return {
    requiresTwoFactor: false,
    token: loginResponse.token,
  };
};

/**
 * Handle 2FA verification and login
 */
export const handleTwoFactorLogin = async (
  email: string,
  password: string,
  code: string,
  nextUrl?: string
): Promise<{ token?: string }> => {
  // Step 1: Verify 2FA code
  await authApi.verifyTwoFactorCode({ email, password, code });

  // Step 2: Perform login
  const loginResponse = await authApi.login({ email, password });

  // Step 3: Store login response
  await storeLoginResponse(loginResponse);
  if (loginResponse.token) {
    await storeToken(loginResponse.token);
  }
  if (loginResponse.refresh) {
    await storeRefreshToken(loginResponse.refresh);
  }
  if (loginResponse.expires) {
    await storeTokenExpires(loginResponse.expires);
  }

  return {
    token: loginResponse.token,
  };
};

/**
 * Resend 2FA code
 */
export const resendTwoFactorCode = async (
  credentials: LoginCredentials
): Promise<void> => {
  await authApi.sendTwoFactorCode(credentials);
};
